import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getProfile, updateProfile, type Profile } from "@/lib/profile";

/**
 * Unified profile hook - single source of truth for profile data
 * All components should use this instead of fetching profile directly
 */
export function useProfile(userId?: string) {
  const query = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      console.log(`[useProfile] Fetching profile for userId: ${userId}`);
      const profile = await getProfile(supabase, userId!);
      console.log(`[useProfile] Profile fetched:`, {
        userId,
        profileId: profile?.id,
        username: profile?.username,
        usernameType: typeof profile?.username,
        hasAvatar: !!profile?.avatar_url,
        isPremium: profile?.is_premium,
        allKeys: profile ? Object.keys(profile) : [],
      });
      return profile;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Keep data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't refetch on mount if data is in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus to prevent flickering
    placeholderData: (previousData) => {
      if (previousData) {
        console.log(`[useProfile] Using cached profile for userId: ${userId}`, {
          username: previousData.username,
        });
      }
      return previousData;
    },
  });

  // Log when profile data becomes undefined (cache cleared)
  if (userId && !query.isLoading && !query.data) {
    console.warn(`[useProfile] Profile data is missing for userId: ${userId}. Cache may have been cleared.`);
  }

  return query;
}

/**
 * Mutation hook for updating profile with automatic cache invalidation
 */
export function useUpdateProfile(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Partial<Omit<Profile, "id">>) =>
      updateProfile(supabase, userId, updates),
    onSuccess: (freshProfile) => {
      // Update the cache with fresh data
      queryClient.setQueryData(["profile", userId], freshProfile);
      // Invalidate to trigger refetch in other components
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
    },
  });
}
